import {
  Divider,
  Header,
  IconQuestion,
  Space,
  Text,
  WithTooltip,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  type ReportAccessDetailModeEnum,
  ReportAccessDetailModes,
} from '../../enums'

export interface IReportAccessDetailHeaderProps {
  mode: ReportAccessDetailModeEnum
  secondaryCategoryName?: string
  primaryCategoryName?: string
}

/**
 * Presentational Feature Set subcomponent — the right-pane heading swaps by
 * mode: SecondaryCategory name + PrimaryCategory sub-line / "All
 * {SecondaryCategories}" / "All {PrimaryCategories}" — with per-mode helper
 * text and a help icon whose tooltip notes the superuser exclusion.
 */
export const ReportAccessDetailHeader = ({
  mode,
  secondaryCategoryName,
  primaryCategoryName,
}: IReportAccessDetailHeaderProps) => {
  const intl = useProcurifyIntl()

  const copyByMode: Record<
    ReportAccessDetailModeEnum,
    { heading: string; helperText: string; subheading?: string }
  > = {
    [ReportAccessDetailModes.ALL_PRIMARY_CATEGORIES]: {
      heading: intl.formatMessage(messages.allPrimaryCategoriesHeading),
      helperText: intl.formatMessage(messages.allPrimaryCategoriesHelperText),
    },
    [ReportAccessDetailModes.ALL_SECONDARY_CATEGORIES]: {
      heading: intl.formatMessage(messages.allSecondaryCategoriesHeading),
      helperText: intl.formatMessage(
        messages.allSecondaryCategoriesHelperText,
        { primaryCategoryName }
      ),
    },
    [ReportAccessDetailModes.SINGLE_SECONDARY_CATEGORY]: {
      heading: secondaryCategoryName ?? '',
      helperText: intl.formatMessage(messages.singleHelperText, {
        secondaryCategoryName,
      }),
      subheading: primaryCategoryName,
    },
  }
  const { heading, helperText, subheading } = copyByMode[mode]

  return (
    <Space size='small'>
      <Header as='h4'>{heading}</Header>
      {subheading && <Text color='textSecondary'>{subheading}</Text>}
      <Divider />
      <Text>
        {helperText}
        <WithTooltip
          placement='top'
          title={intl.formatMessage(messages.superuserExclusionTooltip)}
        >
          <WithTooltip.Wrapper tabIndex={0}>
            <IconQuestion
              size={16}
              ml={0.25}
              ariaLabel={intl.formatMessage(messages.helpIconLabel)}
            />
          </WithTooltip.Wrapper>
        </WithTooltip>
      </Text>
    </Space>
  )
}

ReportAccessDetailHeader.displayName = 'ReportAccessDetailHeader'
